// AuditPages.tsx — /liff/audit/patrol + /liff/audit/log
//
// Both pages are read-only views that mount with `RoleGate allow=["auditor",
// "tenant_admin"]`. The auditor role is read-only by design (defense-in-
// depth: backend repeats the check). Patrol view shows N most recent
// patrol traversals; Log view shows the nexus_audit_*.audit_log feed.

import React, { useEffect, useState } from "react";
import { useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import {
  MobileShell, Card, CardHeader, MobileSpinner, EmptyMobile, ListRow,
  ComingSoonCard, SecondaryButton,
} from "../../../liff/components/Mobile.js";

interface PatrolAuditRow {
  id: string;
  occurred_at_local: string;
  site_name: string;
  officer_name: string;
  status: "completed" | "missed" | "skipped" | "incident";
  duration_minutes?: number;
}

interface PatrolAuditResponse {
  items: PatrolAuditRow[];
}

/** /liff/audit/patrol — paginated patrol traversals (newest-first). */
export function AuditPatrolPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<PatrolAuditResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/audit/patrols?tenant_id=${encodeURIComponent(session.tenant_id)}&limit=50`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) setData(await r.json() as PatrolAuditResponse);
        else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  if (error) return (
    <MobileShell title="巡邏記錄"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>
  );
  if (!data) return <MobileSpinner message="載入巡邏紀錄" />;

  return (
    <MobileShell title="巡邏記錄" subtitle="稽核唯讀檢視">
      <Card>
        <CardHeader title="近期巡邏" hint={`${data.items.length} 筆`} />
        {data.items.length === 0 ? (
          <EmptyMobile title="目前沒有巡邏紀錄" />
        ) : (
          <ul className="space-y-2">
            {data.items.map((r) => {
              const tone = r.status === "completed" ? "text-success" : r.status === "missed" ? "text-danger" : r.status === "incident" ? "text-warning" : "text-text-muted";
              return (
                <li key={r.id} className="rounded-xl bg-bg-raised p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-sm font-medium truncate">{r.site_name}</p>
                    <span className={`text-[10px] font-semibold uppercase ${tone}`}>
                      {r.status === "completed" ? "完成" : r.status === "missed" ? "漏巡" : r.status === "skipped" ? "略過" : "事件"}
                    </span>
                  </div>
                  <p className="text-[11px] text-text-muted">
                    {r.occurred_at_local} · {r.officer_name}
                    {r.duration_minutes !== undefined && ` · ${r.duration_minutes} 分`}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      <SecondaryButton href="/liff/incidents">查看相關事件</SecondaryButton>
    </MobileShell>
  );
}

interface AuditLogRow {
  id: string;
  at_local: string;
  actor: string;
  action: string;
  target?: string;
  correlation_id: string;
}

interface AuditLogResponse {
  items: AuditLogRow[];
}

/** /liff/audit/log — high-level audit_log feed. */
export function AuditLogPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<AuditLogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/audit/log?tenant_id=${encodeURIComponent(session.tenant_id)}&limit=50`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) setData(await r.json() as AuditLogResponse);
        else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  if (error) return (
    <MobileShell title="操作紀錄"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>
  );
  if (!data) return <MobileSpinner message="載入稽核日誌中…" />;

  return (
    <MobileShell title="操作紀錄" subtitle="系統稽核軌跡">
      <Card>
        <CardHeader title="近期事件" hint={`${data.items.length} 筆`} />
        {data.items.length === 0 ? (
          <EmptyMobile title="近期無稽核事件" />
        ) : (
          <ul className="space-y-2">
            {data.items.map((r) => (
              <li key={r.id} className="rounded-xl bg-bg-raised p-3 font-mono text-[11px]">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-accent">{r.action}</span>
                  <span className="text-text-muted">{r.at_local}</span>
                </div>
                <div className="text-text-secondary truncate">{r.actor} {r.target && `→ ${r.target}`}</div>
                <div className="text-text-muted truncate">cid {r.correlation_id.slice(0, 12)}…</div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ComingSoonCard
        title="操作紀錄 檢索"
        description="按 actor / 時間 / action / target 檢索的進階檢視。"
        willShow={["按 actor 篩選", "時間區間 + 關聯 correlation_id 串連", "匯出為 CSV", "PII 自動脫敏"]}
      />
    </MobileShell>
  );
}
